import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IOnlineTourPriceResult } from '../utils/types';
import { calculatePrice, ChildInfo } from '../utils';
import { NumberOfGuestsSelector } from './NumberOfGuestsSelector';
import { ReservationOptionSkeleton } from './ReservationOptionSkeleton';
import { ReservationOption } from './ReservationOption';

export function TourPurchaseReservationOptions({
  guests,
  setGuests,
  adultPrices,
  allowSharedRooming,
  pricesLoading,
}: {
  guests: {
    adults: number;
    children: number;
    childrenInfo: ChildInfo[];
    priceId: number | null;
  };
  setGuests: (value: {
    adults: number;
    children: number;
    childrenInfo: ChildInfo[];
    priceId: number | null;
  }) => void;
  adultPrices: {
    price: IOnlineTourPriceResult;
    childrenPrices: IOnlineTourPriceResult[];
    bedType: string;
  }[];
  allowSharedRooming: boolean;
  pricesLoading: boolean;
}) {
  const { t } = useTranslation();

  function handleGuestsChange(value: {
    adults: number;
    children: number;
    childrenInfo: ChildInfo[];
  }) {
    setGuests({
      ...value,
      priceId: null,
    });
  }

  return (
    <Box>
      <Typography gutterBottom sx={{ color: 'text.secondary' }}>
        {t('tourPurchasePage.numberOfGuests')}
      </Typography>
      <NumberOfGuestsSelector value={guests} onChange={handleGuestsChange} />

      <Typography sx={{ mt: 3 }} gutterBottom variant="h6" component="h2">
        {t('tourPurchasePage.options')}
      </Typography>

      {pricesLoading &&
        Array(5)
          .fill(0)
          .map((_, i) => <ReservationOptionSkeleton key={i} />)}

      {adultPrices &&
        adultPrices.map((price, i) => {
          const { fitting, priceOk, adultPrice, childrenPrice, extraBeds } =
            calculatePrice(guests, price, { allowSharedRooming });

          return (
            <ReservationOption
              key={price.price.id}
              title={price.bedType}
              description={
                fitting && priceOk
                  ? t('tourPurchasePage.adults', {
                      count: guests.adults,
                    }) +
                    (guests.children > 0
                      ? ' - ' +
                        t('tourPurchasePage.children', {
                          count: guests.children,
                        })
                      : '')
                  : ''
              }
              price={priceOk ? adultPrice + childrenPrice : null}
              currencyCode={price.price.currencyCode}
              selected={guests.priceId === price.price.id}
              onClick={() => {
                setGuests({
                  ...guests,
                  priceId: price.price.id,
                });
                window.scrollTo({
                  top: document.body.scrollHeight,
                  behavior: 'smooth',
                });
              }}
              sx={{ mb: 1 }}
              disabled={!priceOk || !fitting}
              message={
                (!priceOk && t('tourPurchasePage.priceNotAvailable')) ||
                (!fitting &&
                  (extraBeds > 0
                    ? t('tourPurchasePage.inconvinientCapacityNoSingle')
                    : t('tourPurchasePage.inconvinientCapacity'))) ||
                (extraBeds > 0 && t('tourPurchasePage.sharedRooming')) ||
                ''
              }
              error={!priceOk || !fitting}
            />
          );
        })}
    </Box>
  );
}
